import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Wordmark, Logo } from "@/components/Brand";
import ThemeToggle from "@/components/ThemeToggle";
import InstallButton from "@/components/InstallButton";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { RoleBadges } from "@/components/RoleBadges";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { initials } from "@/lib/format";
import {
  LayoutDashboard,
  Users,
  UserCircle,
  UserPlus,
  ShieldCheck,
  BarChart3,
  Menu,
  LogOut,
  Home,
  Radio,
  Scissors,
  Trophy,
  Clapperboard,
  Settings2,
  Mic,
  LifeBuoy,
} from "lucide-react";

const MEMBER_LINKS = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/portal/hub", label: "Podcast Hub", icon: Radio },
  { to: "/portal/directory", label: "Member Directory", icon: Users },
  { to: "/portal/clips", label: "Shared Clips", icon: Scissors },
  { to: "/portal/rewards", label: "Rewards", icon: Trophy },
  { to: "/portal/invite", label: "Invite a Member", icon: UserPlus, testId: "nav-invite" },
  { to: "/portal/account", label: "My Account", icon: UserCircle, testId: "nav-account" },
  { to: "/portal/guides", label: "Help & Guides", icon: LifeBuoy, testId: "nav-guides" },
];

// Each elevated link declares the capability that reveals it.
const STUDIO_LINKS = [
  { to: "/studio", label: "Studio", icon: Clapperboard, cap: "producer_tools", testId: "nav-studio" },
  { to: "/admin/studio", label: "Library & Tools", icon: Radio, cap: "producer_tools" },
];
const LEADERSHIP_LINKS = [
  { to: "/admin/members", label: "Members", icon: ShieldCheck, cap: "view_members", badge: "members" },
  { to: "/portal/voice", label: "Voice Room", icon: Mic, cap: "view_members" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, cap: "view_analytics" },
];
const ADMIN_LINKS = [
  { to: "/admin/manage", label: "Manage", icon: Settings2, cap: "manage_events", badge: "inquiry" },
];

const itemClass = ({ isActive }) =>
  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
    isActive
      ? "bg-secondary font-semibold text-foreground"
      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
  }`;

function NavItems({ links, onNavigate, badges = {} }) {
  return links.map((l) => (
    <NavLink
      key={l.to}
      to={l.to}
      end={l.end}
      className={itemClass}
      onClick={onNavigate}
      data-testid={l.to.startsWith("/admin") ? `nav-admin-${l.label.split(" ")[0].toLowerCase()}` : (l.testId || `nav-${l.label.split(" ")[0].toLowerCase()}`)}
    >
      <l.icon className="h-[18px] w-[18px]" />
      {l.label}
      {l.badge && badges[l.badge] > 0 && (
        <Badge className="ml-auto h-5 min-w-5 justify-center border border-[rgba(201,119,46,0.45)] bg-[rgba(201,119,46,0.22)] px-1.5 text-[11px] text-[#8A4A18]" data-testid={`nav-${l.badge}-badge`}>
          {badges[l.badge]}
        </Badge>
      )}
    </NavLink>
  ));
}

function SectionHeader({ children }) {
  return <p className="px-3 pb-1 pt-5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</p>;
}

function SidebarNav({ onNavigate, can, badges = {} }) {
  const studio = STUDIO_LINKS.filter((l) => can(l.cap));
  const leadership = LEADERSHIP_LINKS.filter((l) => can(l.cap));
  const admin = ADMIN_LINKS.filter((l) => can(l.cap));
  return (
    <nav className="flex flex-col gap-1">
      <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Member</p>
      <NavItems links={MEMBER_LINKS} onNavigate={onNavigate} badges={badges} />

      {studio.length > 0 && (
        <>
          <SectionHeader>Producer</SectionHeader>
          <NavItems links={studio} onNavigate={onNavigate} badges={badges} />
        </>
      )}
      {leadership.length > 0 && (
        <>
          <SectionHeader>Leadership</SectionHeader>
          <NavItems links={leadership} onNavigate={onNavigate} badges={badges} />
        </>
      )}
      {admin.length > 0 && (
        <>
          <SectionHeader>Admin</SectionHeader>
          <NavItems links={admin} onNavigate={onNavigate} badges={badges} />
        </>
      )}
      <InstallButton variant="outline" className="mt-5 w-full" />
    </nav>
  );
}

export default function PortalLayout() {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [inquiryNew, setInquiryNew] = useState(0);
  const [podcastReqs, setPodcastReqs] = useState(0);
  const [joinPending, setJoinPending] = useState(0);

  useEffect(() => {
    let active = true;
    if (can("manage_inquiries")) {
      api.get("/inquiries/stats").then((r) => { if (active) setInquiryNew(r.data?.new || 0); }).catch(() => {});
    }
    if (can("view_members")) {
      api.get("/admin/podcast-requests/stats").then((r) => { if (active) setPodcastReqs(r.data?.pending || 0); }).catch(() => {});
      api.get("/admin/join-requests/stats").then((r) => { if (active) setJoinPending(r.data?.pending || 0); }).catch(() => {});
    }
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const badges = { inquiry: inquiryNew, members: podcastReqs + joinPending };

  // New members finish onboarding before entering the portal.
  if (user && user.onboarding_completed === false) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
      <div className="min-h-screen bg-background" style={{ "--portal-topbar-offset": "64px" }}>
        {/* Top bar */}
        <header className="sticky top-0 z-30 border-b border-border bg-card">
          <div className="flex h-16 items-center justify-between gap-3 px-4 sm:px-6">
            <div className="flex items-center gap-2">
              <div className="lg:hidden">
                <Sheet open={open} onOpenChange={setOpen}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label="Open navigation" data-testid="portal-mobile-menu">
                      <Menu className="h-5 w-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-72 overflow-y-auto bg-card p-4">
                    <div className="mb-4"><Wordmark /></div>
                    <SidebarNav onNavigate={() => setOpen(false)} can={can} badges={badges} />
                  </SheetContent>
                </Sheet>
              </div>
              <div className="hidden lg:block"><Wordmark /></div>
              <Link to="/" className="lg:hidden"><Logo /></Link>
            </div>

            <div className="flex items-center gap-3">
              <ThemeToggle />
              <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
                <Link to="/"><Home className="mr-2 h-4 w-4" /> View site</Link>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 rounded-full p-0.5 outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="portal-user-menu">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={user?.avatar || user?.picture || undefined} alt={user?.name} />
                      <AvatarFallback className="bg-primary text-primary-foreground text-sm">{initials(user?.name)}</AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="font-semibold">{user?.name}</span>
                      <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <div className="px-2 pb-1.5">
                    <RoleBadges roles={user?.roles} is_owner={user?.is_owner} podcast_eligible={user?.podcast_eligible} showMember size="sm" />
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/portal/account/profile")}>Profile & Privacy</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/portal/account/business")}>My Business</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="portal-logout" className="text-destructive focus:text-destructive">
                    <LogOut className="mr-2 h-4 w-4" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <div className="mx-auto grid w-full max-w-[1400px] lg:grid-cols-[260px_1fr]">
          <aside className="hidden border-r border-border bg-card lg:block">
            <div className="sticky top-16 max-h-[calc(100vh-4rem)] overflow-y-auto p-4">
              <SidebarNav can={can} badges={badges} />
            </div>
          </aside>
          <main className="min-w-0 px-4 py-6 pb-24 sm:px-6 lg:px-8">
            <Outlet />
          </main>
        </div>

      </div>
  );
}
